/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, BoxView, ActionButton } from '@components/atoms'
import { DealerList } from '@components/organisms/dealerPrice/DealerList'
import { DealerPriceSheet } from '@components/organisms/dealerPrice/DealerPriceSheet'
import { mono, boShared, boMedia } from '@components/systems/backoffice/backoffice.tokens'
import { BoSplit } from '@components/systems/backoffice/BoSplit'

import { useAppDispatch, useAppSelector } from '@redux/index'
import { dealerPriceActions } from '@redux/store/index'
import { DealerType, DealerPriceSaveType } from '@schemas'

/**
 * 납품가 관리 (`/main/dealer-price`) — DISPATCH_PLAN.md §4.
 * 좌: 거래처(역할 DEALER·DISTRIBUTOR) · 우: 업체 정보 + 제품별 납품가 규칙 표.
 * 레이아웃은 /main/product 의 좌우 분할(30rem + 나머지) 그대로.
 */
const DealerPricePage = () => {
	const reduxDispatch = useAppDispatch()

	const dealerList: DealerType[] = useAppSelector(state => state.dealerPrice.dealerList)
	const dealerListStatus = useAppSelector(state => state.dealerPrice.dealerListStatus)
	const selectedCustomerId: string = useAppSelector(
		state => state.dealerPrice.selectedCustomerId,
	)
	const sheetDealer = useAppSelector(state => state.dealerPrice.sheetDealer)
	const sheetRows = useAppSelector(state => state.dealerPrice.sheetRows)
	const sheetStatus = useAppSelector(state => state.dealerPrice.sheetStatus)
	const isSaving: boolean = useAppSelector(state => state.dealerPrice.isSaving)
	const savedCount: number = useAppSelector(state => state.dealerPrice.savedCount)

	const [isEditMode, setIsEditMode] = React.useState<boolean>(false)
	const [resetKey, setResetKey] = React.useState<number>(0)

	// 들어오면 목록을 반드시 한 번 읽는다(저장된 상태를 믿지 않는다 — Order.page 와 같은 이유)
	const didInit = React.useRef<boolean>(false)
	React.useEffect(() => {
		if (didInit.current) return
		didInit.current = true
		reduxDispatch(dealerPriceActions.TRIGGER_INIT_DEALER_PRICE_PAGE(true))
		if (selectedCustomerId !== '') {
			reduxDispatch(dealerPriceActions.SELECT_DEALER(selectedCustomerId))
		}
	}, [reduxDispatch, selectedCustomerId])

	// 저장이 끝나면 편집 모드를 닫는다
	React.useEffect(() => {
		if (savedCount > 0) setIsEditMode(false)
	}, [savedCount])

	const onSelect = (dealer: DealerType) => {
		setIsEditMode(false)
		reduxDispatch(dealerPriceActions.SELECT_DEALER(dealer.customerId))
	}

	const onToggleEdit = () => {
		if (isEditMode) setResetKey(k => k + 1)
		setIsEditMode(!isEditMode)
	}

	const onSave = (body: DealerPriceSaveType) => {
		if (sheetDealer === null) return
		reduxDispatch(
			dealerPriceActions.SAVE_DEALER_PRICE({ customerId: sheetDealer.customerId, body }),
		)
	}

	const sheetTitle =
		sheetDealer === null
			? '납품가'
			: `납품가 — ${
					sheetDealer.companyName !== '' ? sheetDealer.companyName : sheetDealer.name
			  }`

	return (
		<View css={style}>
			<BoSplit
				className="dealerOuterView"
				title="납품가"
				detailKey={selectedCustomerId}
				list={
					<BoxView title="거래처" className="dealerView">
						<View className="listOuterView">
							{dealerListStatus === 'loading' ? (
								<p className="boEmpty">Loading...</p>
							) : (
								<DealerList
									dealers={dealerList}
									selectedCustomerId={selectedCustomerId}
									onSelect={onSelect}
								/>
							)}
						</View>
					</BoxView>
				}
				detail={
					<BoxView
						title={sheetTitle}
						className="sheetView"
						rightHeaderAccessory={
							sheetDealer !== null && (
								<ActionButton
									title={isEditMode ? '수정 종료' : '수정'}
									iconName={isEditMode ? 'pencil-off-outline' : 'pencil-outline'}
									variant="outlined"
									onClick={onToggleEdit}
								/>
							)
						}
					>
						{sheetStatus === 'loading' ? (
							<p className="boEmpty">Loading...</p>
						) : sheetDealer !== null ? (
							<DealerPriceSheet
								dealer={sheetDealer}
								rows={sheetRows}
								isEditMode={isEditMode}
								isSaving={isSaving}
								resetKey={resetKey}
								onSave={onSave}
							/>
						) : (
							<p className="boEmpty">거래처를 고르면 제품별 납품가가 여기 섭니다.</p>
						)}
					</BoxView>
				}
			/>
		</View>
	)
}

export default DealerPricePage

const style = css({
	...boShared,
	height: '100%',

	'.dealerOuterView': {
		position: 'relative',
		display: 'flex',
		height: '100%',

		'.listOuterView': {
			width: '100%',
			height: '100%',
			overflowY: 'auto',
		},

		'.dealerView': {
			width: '30rem',
			paddingRight: 24,
			borderRight: `1px solid ${mono.line}`,
		},
		'.sheetView': {
			width: 'calc(100% - 30rem)',
			paddingLeft: 24,
			overflowY: 'auto',
		},

		[boMedia.notDesktop]: {
			flexDirection: 'column',
			'.dealerView': {
				width: '100%',
				paddingRight: 0,
				borderRight: 0,
				borderBottom: `1px solid ${mono.line}`,
				marginBottom: 16,
			},
			'.sheetView': { width: '100%', paddingLeft: 0, overflowX: 'auto' },
		},
	},
})
